import debug from 'debug';

// GQL
import { client } from '../client';
import { VEHICLE_OCCUPANTS_BY_IDS_WARMUP } from '../queries/vehicles';

const log = debug('rois:gql:selectors:warmup');

// Límite del contrato backend: `vehicleOccupantsByIds` acepta máx 200 ids
// por llamada. Una action ronda 1-3 ocupantes, el troceo es defensivo.
const BATCH_SIZE = 200;

/**
 * Warm-up de la caché normalizada de Apollo para los VehicleOccupant del
 * resumen de actuación (`selectActionById`) y del detalle de OT/campaña.
 *
 * Resuelve los ocupantes en UNA sola query `vehicleOccupantsByIds`
 * (selection-set = el de GET_VEHICLE_OCCUPANT_BY_ID = type completo; el
 * select backend proyecta exactamente esos campos, con tripwire
 * bidireccional en rois-back). Las entidades `VehicleOccupant:<id>` quedan
 * normalizadas en caché, de modo que los
 * `client.query(GET_VEHICLE_OCCUPANT_BY_ID, cache-first)` per-row que
 * vienen después se sirven de caché vía el redirect
 * `Query.vehicleOccupant → toReference` de policies.ts, en lugar de
 * disparar O requests fila a fila (el N+1 GetVehicleOccupantById×20
 * restante tras el Tier A del detalle).
 *
 * REGLA DE ORO — es una OPTIMIZACIÓN, jamás un gate: si falla (tablet
 * offline, backend viejo sin la query batch, error GraphQL, lo que sea) se
 * ignora en silencio y el selector sigue EXACTAMENTE como hoy — los
 * per-row GET_VEHICLE_OCCUPANT_BY_ID intactos son el fallback natural.
 * Este helper NUNCA lanza.
 *
 * `fetchPolicy` (default `cache-first`, contrato de tablas): los flujos SIN
 * mecanismo de refresco posterior (resumen de actuación, detalle) pasan
 * `network-only` — los ocupantes son datos transaccionales (cambian al
 * editar operarios/vehículos de la action sin cambiar los ids), así que la
 * frescura de cada carga la garantiza el propio warm-up, igual que hacía
 * el per-row network-only al que sustituye.
 */
export async function warmupVehicleOccupantsByIds(
  ids: (string | undefined | null)[],
  reFetch = false,
  fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'
): Promise<void> {
  // Con reFetch=true el per-row va network-only e IGNORA la caché: el
  // warm-up solo añadiría una request extra con los mismos payloads que
  // las respuestas per-row ya traen. Solo aporta en el camino cache-first.
  if (reFetch) return;
  try {
    const uniqueIds = [
      ...new Set(ids.filter((id): id is string => Boolean(id))),
    ];
    if (uniqueIds.length === 0) return;

    const batches: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
      batches.push(uniqueIds.slice(i, i + BATCH_SIZE));
    }

    await Promise.all(
      batches.map((batch) =>
        client.query({
          query: VEHICLE_OCCUPANTS_BY_IDS_WARMUP,
          variables: { ids: batch },
          fetchPolicy,
          // 'all' cubre "backend desplegado sin vehicleOccupantsByIds"
          // (campo desconocido → error GraphQL → se ignora, no revienta).
          errorPolicy: 'all',
        })
      )
    );
  } catch (err) {
    // VACÍO de efectos a propósito: warm-up fallido = comportamiento de hoy.
    log('warm-up vehicleOccupantsByIds omitido (sin efectos):', err);
  }
}
